import { useState } from 'react';
import { usePaymentStore, useUIStore, useAuthStore } from '../store';
import { usePayment } from '../hooks/usePayment';
import { Button, LoadingSpinner } from './ui';
import { PaymentSuccess } from './PaymentSuccess';
import { PaymentErrorModal } from './PaymentErrorModal';
import { formatCurrency, formatDate } from '../utils/formatters';

export const PaymentFlow = () => {
  const { selectedCharge } = usePaymentStore();
  const { setActiveView } = useUIStore();
  const { user } = useAuthStore();
  const { paymentState, initiatePayment, clearError } = usePayment();
  
  // Local state for payment options
  const [activeTab, setActiveTab] = useState<'full' | 'partial'>('full');
  const [partialAmount, setPartialAmount] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleBack = () => {
    setActiveView('dashboard');
  };

  // If no charge selected, redirect back to dashboard
  if (!selectedCharge) {
    setActiveView('dashboard');
    return null;
  }

  // Get payment amount based on active tab
  const getPaymentAmount = (): number => {
    const actualOutstanding = selectedCharge.outstandingAmount;
    
    if (activeTab === 'full') {
      return actualOutstanding;
    } else {
      const amount = parseFloat(partialAmount) || 0;
      return amount;
    }
  };

  const validatePayment = (): boolean => {
    const paymentAmount = getPaymentAmount();
    const actualOutstanding = selectedCharge.outstandingAmount;
    
    if (paymentAmount <= 0) {
      setValidationError('Payment amount must be greater than ₹0.');
      return false;
    }

    if (activeTab === 'partial') {
      if (paymentAmount < 50) {
        setValidationError('Minimum partial payment amount is ₹50.');
        return false;
      }
      
      if (paymentAmount > actualOutstanding) {
        setValidationError(`Partial payment cannot exceed charge outstanding amount of ${formatCurrency(actualOutstanding)}.`);
        return false;
      }
    }

    setValidationError(null);
    return true;
  };

  const handlePayment = async () => {
    if (!validatePayment()) {
      return;
    }

    const paymentAmount = getPaymentAmount();
    
    // Get flat ID from auth store
    if (!user?.activeFlatId) {
      setValidationError('User authentication error. Please refresh and try again.');
      return;
    }
    
    await initiatePayment(user.activeFlatId, selectedCharge.id, paymentAmount);
  };

  const handleErrorDismiss = () => {
    clearError();
  };

  // Show success screen if payment was successful
  if (paymentState.isSuccess && paymentState.paymentRequestId) {
    return <PaymentSuccess paymentRequestId={paymentState.paymentRequestId} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="p-4">
          <div className="flex items-center">
            <Button
              onClick={handleBack}
              variant="outline"
              size="small"
              className="mr-3"
            >
              ← Back
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Make Payment</h1>
              <p className="text-sm text-gray-600">Complete your payment securely</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Validation Error */}
        {validationError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-800">{validationError}</p>
          </div>
        )}

        {/* Selected Charge Summary */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Selected Charge</p>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{selectedCharge.chargeName}</h2>
            <p className="text-3xl font-bold text-red-600">
              {formatCurrency(selectedCharge.outstandingAmount)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Outstanding Amount</p>
            {selectedCharge.paidAmount && selectedCharge.paidAmount > 0 && (
              <p className="text-sm text-green-600 mt-2">
                Already Paid: {formatCurrency(selectedCharge.paidAmount)}
              </p>
            )}
          </div>
        </div>

        {/* Charge Details */}
        <div className="bg-white rounded-lg shadow-sm border mb-4 p-4">
          <h3 className="font-medium text-gray-900 mb-3">Charge Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Bill Number:</span>
              <span className="font-medium">{selectedCharge.billNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Bill Date:</span>
              <span className="font-medium">{formatDate(selectedCharge.billDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Category:</span>
              <span className="font-medium">{selectedCharge.chargeCategory}</span>
            </div>
            {selectedCharge.paidAmount && selectedCharge.paidAmount > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Paid Amount:</span>
                <span className="font-medium text-green-600">
                  {formatCurrency(selectedCharge.paidAmount)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Payment Tabs */}
        <div className="bg-white rounded-lg shadow-sm border mb-4">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('full')}
              className={`flex-1 px-4 py-3 text-center font-medium ${
                activeTab === 'full'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Pay Full Amount
            </button>
            <button
              onClick={() => setActiveTab('partial')}
              className={`flex-1 px-4 py-3 text-center font-medium ${
                activeTab === 'partial'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Pay Partial Amount
            </button>
          </div>
          
          {/* Tab Content */}
          <div className="p-4">
            {activeTab === 'full' ? (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">You will pay the full outstanding amount for this charge</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(selectedCharge.outstandingAmount)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Partial Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">₹</span>
                    <input
                      type="number"
                      value={partialAmount}
                      onChange={(e) => setPartialAmount(e.target.value)}
                      placeholder="Enter amount"
                      min="50"
                      max={selectedCharge.outstandingAmount}
                      step="1"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={paymentState.isProcessing}
                    />
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mt-2">
                    <span>Minimum: ₹50</span>
                    <span>Maximum: {formatCurrency(selectedCharge.outstandingAmount)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pay Button */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <Button
            onClick={handlePayment}
            variant="primary"
            size="large"
            className="w-full h-14"
            disabled={paymentState.isProcessing}
          >
            {paymentState.isProcessing ? (
              <div className="flex items-center justify-center">
                <LoadingSpinner size="small" />
                <span className="ml-2">Processing Payment...</span>
              </div>
            ) : (
              <span className="text-lg font-bold">
                Pay {formatCurrency(getPaymentAmount())}
              </span>
            )}
          </Button>
          
          {!paymentState.isProcessing && (
            <p className="text-xs text-gray-500 text-center mt-2">
              Your payment will be processed securely through Razorpay
            </p>
          )}
        </div>
      </div>

      {/* Error Modal */}
      {paymentState.error && (
        <PaymentErrorModal
          error={paymentState.error}
          onDismiss={handleErrorDismiss}
          onRetry={() => {
            clearError();
            // User can click pay button again to retry
          }}
        />
      )}
    </div>
  );
};